import React, { useMemo, useState } from "react";
import { WomCard, WomSegmented } from "./design-system/components";

const translations = {
  en: {
    yourMoodMap: "Your mood map",
    subtitle: "Personal emoji history for the last years.",
    timeRange: "Time range",
    range1y: "1 year",
    range3y: "3 years",
    rangeAll: "All time",
    totalEntries: "Total entries",
    avgMood: "Average mood",
    dominantMood: "Dominant mood",
    tip:
      "Tip: combine this view with the global analytics to spot how your mood follows world events.",
  },
  uk: {
    yourMoodMap: "Твоя карта настрою",
    subtitle: "Особиста історія емодзі за останні роки.",
    timeRange: "Період",
    range1y: "1 рік",
    range3y: "3 роки",
    rangeAll: "Завжди",
    totalEntries: "Усього записів",
    avgMood: "Середній настрій",
    dominantMood: "Домінуючий настрій",
    tip:
      "Порада: поєднуй цю карту з глобальною аналітикою, щоб бачити, як твій настрій реагує на події у світі.",
  },
};

const RANGE_OPTIONS = [
  { value: "1y", label: "1 year" },
  { value: "3y", label: "3 years" },
  { value: "all", label: "All time" },
];

const EMOJI_TO_SCORE = {
  "😁": 2,
  "😊": 1.5,
  "😍": 1,
  "😐": 0,
  "😡": -0.5,
  "😢": -1.5,
};

function getScoreForEmoji(emoji) {
  return EMOJI_TO_SCORE[emoji] ?? 0;
}

// Дістаємо Date з createdAt (Timestamp / Date / plain Date)
function getCreatedAtDate(mood) {
  if (!mood || !mood.createdAt) return null;
  const created = mood.createdAt;
  if (typeof created.toDate === "function") {
    return created.toDate();
  }
  if (created instanceof Date) {
    return created;
  }
  return null;
}

export default function UserMoodLifeMap({ moods, userId, darkTheme, language = "en", }) {
  const t = translations[language] || translations.en;
  // далі твій код як був
  const [range, setRange] = useState("3y");

  const bg = darkTheme ? "rgba(15,23,42,0.98)" : "#ffffff";
  const bgSoft = darkTheme ? "rgba(15,23,42,0.95)" : "#f9fafb";
  const border = darkTheme ? "rgba(148,163,184,0.4)" : "rgba(148,163,184,0.45)";
  const text = darkTheme ? "#e2e8f0" : "#0f172a";
  const textSoft = darkTheme ? "rgba(148,163,184,0.9)" : "rgba(100,116,139,0.95)";

  // 1) Беремо тільки настрії поточного юзера
  const userMoods = useMemo(() => {
    if (!Array.isArray(moods) || !userId) return [];
    return moods
      .filter((m) => m.userId === userId)
      .map((m) => ({ ...m, createdAtDate: getCreatedAtDate(m) }))
      .filter((m) => m.createdAtDate);
  }, [moods, userId]);

  // 2) Фільтр по діапазону (1y / 3y / all)
  const filtered = useMemo(() => {
    const now = new Date();
    let threshold = null;

    if (range === "1y") {
      threshold = new Date(now);
      threshold.setFullYear(now.getFullYear() - 1);
    } else if (range === "3y") {
      threshold = new Date(now);
      threshold.setFullYear(now.getFullYear() - 3);
    } else {
      threshold = null; // all time → без дод. фільтра
    }

    return userMoods.filter(
      (m) => !threshold || m.createdAtDate >= threshold
    );
  }, [userMoods, range]);

  // 3) Прості статистики
  const stats = useMemo(() => {
    if (!filtered.length) {
      return {
        total: 0,
        avgScore: 0,
        dominantEmoji: "—",
      };
    }

    let totalScore = 0;
    const emojiCount = {};

    filtered.forEach((m) => {
      const emoji = m.emoji || m.moodEmoji || "🙂";
      const score =
        typeof m.score === "number" ? m.score : getScoreForEmoji(emoji);
      totalScore += score;
      emojiCount[emoji] = (emojiCount[emoji] || 0) + 1;
    });

    const avgScore = totalScore / filtered.length;

    const dominantEmoji =
      Object.entries(emojiCount).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";

    return {
      total: filtered.length,
      avgScore,
      dominantEmoji,
    };
  }, [filtered]);

    // 3.1) Короткий текст-інсайт для статистики
  const moodInsight = useMemo(() => {
    if (!filtered.length) return "";

    // Якщо записів ще мало – просто заохочення продовжувати
    if (stats.total < 5) {
      return language === "uk"
        ? "Коли назбирається більше записів, ти побачиш короткий підсумок настрою за цей період."
        : "Once you log a few more moods, you'll see a short summary for this period.";
    }

    const avg = stats.avgScore;
    let trendUk;
    let trendEn;

    if (avg > 1) {
      trendUk = "здебільшого позитивний";
      trendEn = "mostly positive";
    } else if (avg < 0) {
      trendUk = "радше важкий";
      trendEn = "rather heavy";
    } else {
      trendUk = "доволі змішаний";
      trendEn = "quite mixed";
    }

    if (language === "uk") {
      return `За цей період твій настрій ${trendUk}. Найчастіше з'являється емодзі ${stats.dominantEmoji}.`;
    }

    return `For this period your mood is ${trendEn}. Your most frequent emoji is ${stats.dominantEmoji}.`;
  }, [filtered.length, stats.total, stats.avgScore, stats.dominantEmoji, language]);


  // 4) Групування по роках і місяцях
  const groupedByYear = useMemo(() => {
    const groups = {};
    filtered.forEach((m) => {
      const d = m.createdAtDate;
      if (!d) return;
      const year = d.getFullYear();
      const month = d.toLocaleString("en", { month: "short" });

      if (!groups[year]) groups[year] = {};
      if (!groups[year][month]) groups[year][month] = [];
      groups[year][month].push(m);
    });

    const sortedYears = Object.keys(groups)
      .map(Number)
      .sort((a, b) => b - a); // нові роки зверху

    return sortedYears.map((year) => ({
      year,
      months: Object.entries(groups[year]).sort(
        (a, b) => new Date(`${b[0]} 1, ${year}`) - new Date(`${a[0]} 1, ${year}`)
      ),
    }));
  }, [filtered]);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        padding: 12,
        boxSizing: "border-box",
        background: bgSoft,
      }}
    >
      <WomCard
        padding="md"
        elevated
        style={{
          height: "100%",
          boxSizing: "border-box",
          borderRadius: 18,
          border: `1px solid ${border}`,
          background: bg,
          color: text,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {/* Header + range */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600 }}>{t.yourMoodMap}</div>
          <div style={{ fontSize: 12, color: textSoft }}>
            {t.subtitle}
          </div>

          <div
            style={{
              marginTop: 6,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              fontSize: 11,
              color: textSoft,
            }}
          >
            <span>{t.timeRange}</span>
            <WomSegmented
              value={range}
              onChange={setRange}
              options={RANGE_OPTIONS.map((opt) => ({
  ...opt,
  label:
    opt.value === "1y"
      ? t.range1y
      : opt.value === "3y"
      ? t.range3y
      : opt.value === "all"
      ? t.rangeAll
      : opt.label,
}))}
            />
          </div>
        </div>

        {/* No data */}
        {!filtered.length ? (
          <div
            style={{
              flex: 1,
              minHeight: 240,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: textSoft,
              fontSize: 13,
            }}
          >
            You have no moods yet. Share your first one on the map ✨
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
              gap: 12,
              minHeight: 260,
              overflow: "hidden",
            }}
          >
            {/* Left: stats */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 8,
      }}
    >
                {/* Total */}
                <div
                  style={{
                    borderRadius: 12,
                    padding: 10,
                    border: `1px solid rgba(148,163,184,0.3)`,
                    background: bgSoft,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 11, color: textSoft }}>
                    {t.totalEntries}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {stats.total}
                  </div>
                </div>

                {/* Avg score */}
                <div
                  style={{
                    borderRadius: 12,
                    padding: 10,
                    border: `1px solid rgba(148,163,184,0.3)`,
                    background: bgSoft,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 11, color: textSoft }}>
                    {t.avgMood}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {stats.avgScore.toFixed(2)}
                  </div>
                  <div style={{ fontSize: 11, color: textSoft }}>score</div>
                </div>

                {/* Dominant emoji */}
                <div
                  style={{
                    borderRadius: 12,
                    padding: 10,
                    border: `1px solid rgba(148,163,184,0.3)`,
                    background: bgSoft,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 11, color: textSoft }}>
                    {t.dominantMood}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {stats.dominantEmoji}
                  </div>
                </div>
              </div>

                            {moodInsight && (
                <div
                  style={{
                    fontSize: 11,
                    color: textSoft,
                    marginTop: 4,
                  }}
                >
                  {moodInsight}
                </div>
              )}

              <div
                style={{
                  fontSize: 11,
                  color: textSoft,
                  marginTop: 2,
                }}
              >
                {t.tip}
              </div>

            </div>

            {/* Right: timeline */}
            <div
              style={{
                borderRadius: 12,
                padding: 10,
                border: `1px solid rgba(148,163,184,0.3)`,
                background: bgSoft,
                overflowY: "auto",
              }}
            >
              {groupedByYear.map(({ year, months }) => (
                <div key={year} style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    {year}
                  </div>
                  {months.map(([month, items]) => (
                    <div
                      key={month}
                      style={{
                        marginBottom: 4,
                        paddingLeft: 4,
                        borderLeft: "1px solid rgba(148,163,184,0.35)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: textSoft,
                          marginBottom: 2,
                        }}
                      >
                        {month}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                        }}
                      >
                        {items.map((m) => {
                          const d = m.createdAtDate;
                          const day =
                            d && d.getDate
                              ? String(d.getDate()).padStart(2, "0")
                              : "";
                          return (
                            <div
                              key={m.id}
                              style={{
                                borderRadius: 999,
                                padding: "4px 8px",
                                background: darkTheme
                                  ? "rgba(15,23,42,0.85)"
                                  : "#e5e7eb",
                                border:
                                  "1px solid rgba(148,163,184,0.45)",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 11,
                              }}
                            >
                              <span style={{ fontSize: 16 }}>
                                {m.emoji || m.moodEmoji || "🙂"}
                              </span>
                              <span style={{ color: textSoft }}>
                                {day}
                                {m.note ? ` · ${m.note}` : ""}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </WomCard>
    </div>
  );
}
